import {
  Card,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  TagGroup,
  Text,
} from '@fluentui/react-components';
import { useMemo } from 'react';

import { AcgPicker } from '../../components/Datalake/AcgPicker/AcgPicker.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import { AccessControlService } from '../../services/accessControlService.js';

import type { WorkspaceRoleEditorProps } from './WorkspaceRoleEditor.interface.js';

export function WorkspaceRoleEditor({
  label,
  description,
  groups,
  onChange,
  disabled = false,
}: WorkspaceRoleEditorProps) {
  const client = useDataHubClient();
  const service = useMemo(() => new AccessControlService(client), [client]);
  const assignedGroupIds = useMemo(
    () => new Set(groups.map((group) => group.id)),
    [groups],
  );

  return (
    <Card aria-label={label}>
      <Text weight="semibold">{label}</Text>
      <Text size={200}>{description}</Text>
      {!disabled && (
        <AcgPicker
          service={service}
          onAdd={(group) => {
            if (!assignedGroupIds.has(group.id)) {
              onChange([...groups, group]);
            }
          }}
        />
      )}
      {groups.length > 0 && (
        <TagGroup
          aria-label={`${label} assigned groups`}
          onDismiss={
            disabled
              ? undefined
              : (_event, data) => {
                  onChange(groups.filter((group) => group.id !== data.value));
                }
          }
        >
          {groups.map((group) => (
            <InteractionTag key={group.id} value={group.id}>
              <InteractionTagPrimary hasSecondaryAction={!disabled}>
                {group.displayName ?? group.id}
              </InteractionTagPrimary>
              {!disabled && (
                <InteractionTagSecondary
                  aria-label={`Remove ${group.displayName ?? group.id}`}
                />
              )}
            </InteractionTag>
          ))}
        </TagGroup>
      )}
    </Card>
  );
}
